// src/utils/dateUtils.js

/**
 * تنسيق التاريخ والوقت بالطريقة العربية
 * 
 * @param {Date} date - كائن التاريخ المراد تنسيقه
 * @param {boolean} includeTime - تضمين الوقت في التنسيق
 * @returns {string} - التاريخ المنسق
 */
export const formatDate = (date, includeTime = true) => {
    if (!date || !(date instanceof Date)) {
      return "غير متوفر";
    }
  
    try {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long',
      };
  
      if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
      }
  
      return date.toLocaleDateString('ar-EG', options);
    } catch (error) {
      console.error('خطأ في تنسيق التاريخ:', error);
      
      // تنسيق احتياطي في حالة الخطأ
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const dateStr = `${day}/${month}/${year}`;
      
      if (includeTime) {
        const hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${dateStr} ${hours}:${minutes}`;
      }
      
      return dateStr;
    }
  };
  
  /**
   * حساب الوقت المنقضي منذ تاريخ معين
   * 
   * @param {Date} date - التاريخ المراد حساب الوقت المنقضي منذه
   * @returns {string} - الوقت المنقضي بصيغة نصية
   */
  export const timeAgo = (date) => {
    if (!date) return "غير معروف";
  
    const now = new Date();
    const secondsAgo = Math.floor((now - date) / 1000);
  
    if (secondsAgo < 60) {
      return "منذ لحظات";
    }
  
    const minutesAgo = Math.floor(secondsAgo / 60);
    if (minutesAgo < 60) {
      return `منذ ${minutesAgo} دقيقة`;
    }
  
    const hoursAgo = Math.floor(minutesAgo / 60);
    if (hoursAgo < 24) {
      return `منذ ${hoursAgo} ساعة`;
    }
  
    const daysAgo = Math.floor(hoursAgo / 24);
    if (daysAgo < 30) {
      return `منذ ${daysAgo} يوم`;
    }
  
    const monthsAgo = Math.floor(daysAgo / 30);
    if (monthsAgo < 12) {
      return `منذ ${monthsAgo} شهر`;
    }
  
    const yearsAgo = Math.floor(monthsAgo / 12);
    return `منذ ${yearsAgo} سنة`;
  };
  
  /**
   * تحويل تاريخ Firestore إلى كائن Date
   * 
   * @param {Object} firestoreDate - كائن التاريخ من Firestore
   * @returns {Date|null} - كائن Date أو null إذا كان التاريخ غير صالح
   */
  export const fromFirestoreDate = (firestoreDate) => {
    if (!firestoreDate) return null;
    if (firestoreDate instanceof Date) return firestoreDate;
    
    if (firestoreDate.toDate && typeof firestoreDate.toDate === 'function') {
      try {
        return firestoreDate.toDate();
      } catch (error) {
        console.error('خطأ في تحويل تاريخ Firestore:', error);
        return null;
      }
    }
    
    // محاولة تحويل الطوابع الزمنية
    if (firestoreDate.seconds !== undefined && firestoreDate.nanoseconds !== undefined) {
      try {
        return new Date(firestoreDate.seconds * 1000);
      } catch (error) {
        console.error('خطأ في تحويل الطابع الزمني:', error);
        return null;
      }
    }
    
    return null;
  };
  
  /**
   * تنسيق التاريخ بطريقة ودية للمستخدم
   * 
   * @param {Date|Object} date - التاريخ المراد تنسيقه (يمكن أن يكون كائن Date أو كائن Firestore)
   * @returns {string} - التاريخ المنسق
   */
  export const formatDateFriendly = (date) => {
    // تحويل التاريخ إلى كائن Date إذا كان من Firestore
    const dateObj = date instanceof Date ? date : fromFirestoreDate(date);
    
    if (!dateObj) return "غير متوفر";
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const dateOnly = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    
    // تنسيق الوقت
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}`;
    
    // إذا كان اليوم
    if (dateOnly.getTime() === today.getTime()) {
      return `اليوم، ${timeStr}`;
    }
    
    // إذا كان الأمس
    if (dateOnly.getTime() === yesterday.getTime()) {
      return `الأمس، ${timeStr}`;
    }
    
    // إذا كان خلال الأسبوع الحالي
    const daysDiff = Math.floor((today - dateOnly) / (1000 * 60 * 60 * 24));
    if (daysDiff < 7) {
      const daysOfWeek = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      return `${daysOfWeek[dateObj.getDay()]}، ${timeStr}`;
    }
    
    // التنسيق العادي للتواريخ القديمة
    return formatDate(dateObj);
  };